/**
 * Role Permission Service
 *
 * Loads the permissions of the single role the user selected at `/additional-info` into
 * `useUserStore.rolePermissions`. The v2 permission catalogue checks every page and action
 * against that list; `/users/current/permissions` is the union of all of the user's roles and
 * is therefore too wide to gate a session that runs under one chosen role, which is the same
 * reason every permission-gated request carries `x-role`.
 *
 * Failure is never fatal: the list stays empty with status `error`, which denies everything in
 * v2 mode (fail closed) while leaving login and the v1 catalogue untouched.
 */

import { clearSidebarConfigCache } from "@/routers/utils";
import { getRolePermissionNames } from "@/services/role";
import { useUserStore } from "@/stores";
import { useAdditionalInfo } from "@/stores/domains/additionalInfo/useAdditionalInfo";
import { Logger } from "@/utils";
import {
  getLatestRequestToken,
  invalidateInFlightLoads,
  isStaleLoad,
  nextRequestToken,
} from "./rolePermissionLoad";

const selectedRoleId = (): string | null => useAdditionalInfo.getState().role?.value ?? null;

/**
 * Load the selected role's permissions into the user store.
 *
 * @param options.force Reload even when the store already holds this role's permissions
 * @returns The permission names now in the store
 */
export const loadSelectedRolePermissions = async (options?: {
  force?: boolean;
}): Promise<string[]> => {
  const store = useUserStore.getState();
  const roleId = selectedRoleId();

  if (!roleId) {
    // No role picked yet (first login). Nothing to load, and nothing is gated yet either.
    invalidateInFlightLoads();
    store.setRolePermissions(null, [], "idle");
    clearSidebarConfigCache();
    return [];
  }

  const alreadyLoaded =
    store.rolePermissionsRoleId === roleId && store.rolePermissionsStatus === "ready";
  if (alreadyLoaded && !options?.force) {
    return store.rolePermissions;
  }

  const token = nextRequestToken();
  store.setRolePermissionsStatus("loading");

  const isSuperseded = (): boolean =>
    isStaleLoad({
      token,
      latestToken: getLatestRequestToken(),
      requestedRoleId: roleId,
      currentRoleId: selectedRoleId(),
    });

  try {
    const permissions = await getRolePermissionNames(roleId);

    if (isSuperseded()) {
      Logger.info("Discarded a superseded role permission load", { roleId });
      return useUserStore.getState().rolePermissions;
    }

    useUserStore.getState().setRolePermissions(roleId, permissions, "ready");
    clearSidebarConfigCache();
    Logger.info("Loaded permissions for selected role", {
      roleId,
      permissionsCount: permissions.length,
    });
    return permissions;
  } catch (error) {
    if (isSuperseded()) {
      Logger.info("Discarded a superseded role permission load that failed", { roleId });
      return useUserStore.getState().rolePermissions;
    }

    Logger.error("Failed to load permissions for selected role:", error);
    useUserStore.getState().setRolePermissions(roleId, [], "error");
    clearSidebarConfigCache();
    return [];
  }
};

/**
 * Retry a failed load, ignoring the in-memory cache
 * @returns The permission names now in the store
 */
export const retrySelectedRolePermissions = (): Promise<string[]> =>
  loadSelectedRolePermissions({ force: true });
